import { motion } from "framer-motion";
import { Search, Gauge, MessageCircle } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/anim/Reveal";
import { HE } from "@/lib/i18n";
import { t } from "@/lib/strings";

const STEPS_EN = [
  { icon: Search, n: "01", title: "Enter your business or site", desc: <>Type your website — or just your <strong>business name</strong> — and run the free test in <strong>one click. No signup</strong>, no card.</> },
  { icon: Gauge, n: "02", title: "See your AI score", desc: <>Get an <strong>instant 0–100 visibility score</strong> plus a preview of where AI mentions you.</> },
  { icon: MessageCircle, n: "03", title: "Get the full report", desc: <>Drop your number — the <strong>complete breakdown + fixes</strong> hit your WhatsApp instantly.</> },
];

const STEPS_HE = [
  { icon: Search, n: "01", title: "מזינים שם עסק או אתר", desc: <>מקלידים כתובת אתר — או <strong>רק את שם העסק</strong> — ומריצים את הבדיקה <strong>בקליק אחד. בלי הרשמה</strong>, בלי אשראי.</> },
  { icon: Gauge, n: "02", title: "רואים את ציון ה-AI", desc: <>מקבלים <strong>ציון נראות מיידי <span dir="ltr">0–100</span></strong> + הצצה לאיפה ה-AI מזכיר אתכם.</> },
  { icon: MessageCircle, n: "03", title: "מקבלים את הדוח המלא", desc: <>משאירים מספר וואטסאפ ומקבלים את <strong>הפירוט המלא + התיקונים</strong> ישר לוואטסאפ. מיידי.</> },
];

const STEPS = HE ? STEPS_HE : STEPS_EN;

export const HowItWorks = () => (
  <section id="how-it-works" className="py-20 md:py-28 border-b-2 border-obsidian">
    <div className="av-container">
      <Reveal>
        <div className="flex items-center gap-3 mb-4">
          <div className="h-3 w-3 bg-conversion" />
          <span className="av-label text-obsidian">{t("how.label")}</span>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl text-obsidian max-w-2xl">
          {t("how.title")}
        </h2>
      </Reveal>

      <div className="relative mt-12">
        {/* animated connector line */}
        <motion.div
          className={`hidden md:block absolute top-0 left-0 h-1 bg-conversion z-10 ${HE ? "origin-right" : "origin-left"}`}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: "100%" }}
        />
        <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 border-2 border-obsidian" gap={0.14}>
          {STEPS.map((s, i) => (
            <StaggerItem
              key={s.n}
              className={`p-8 group ${i < 2 ? `border-b-2 md:border-b-0 ${HE ? "md:border-l-2" : "md:border-r-2"} border-obsidian` : ""}`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="border-2 border-obsidian p-3 transition-colors group-hover:bg-conversion group-hover:border-conversion">
                  <s.icon className="h-6 w-6 text-obsidian transition-colors group-hover:text-white" strokeWidth={1.75} />
                </div>
                <span className="font-data text-4xl font-bold text-neutral-200 transition-colors group-hover:text-conversion">{s.n}</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-obsidian mb-2">{s.title}</h3>
              <p className="text-neutral-600 leading-relaxed">{s.desc}</p>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </div>
  </section>
);

export default HowItWorks;
